// Path: src/components/auth/verification/InitiProcess.tsx
// Updated to add missing UserRole import
// Updated to use Ant Design Steps for linear progress, style completed steps/boxes in green, and enhance UI/UX
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Typography, Breadcrumb, Card, Steps, Space } from 'antd';
import { observer } from 'mobx-react';
import { useUserStore } from '../../../stores/UserStore';
import { HomeOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { UserRole } from '@constants/UserEnums';

const { Title, Text } = Typography;
const { Step } = Steps;

const InitiProcessContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  animation: fadeIn 1.5s ease-in;
  padding: 20px;
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const CenteredContent = styled.div`
  padding: 50px;
  text-align: center;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const CenteredTitle = styled(Title)`
  color: #ffaa00;
  margin-bottom: 20px;
  font-size: 28px !important;
  @media (max-width: 768px) {
    font-size: 24px !important;
  }
`;

const StepsWrapper = styled.div`
  margin: 30px 0;
  padding: 0 20px;
  .ant-steps-item-title {
    color: #0427f4;
    font-weight: 500;
  }
  .ant-steps-item-description {
    color: #0427f4;
    font-size: 14px;
  }
  .ant-steps-item-icon {
    font-size: 16px;
  }
  @media (max-width: 768px) {
    padding: 0 10px;
    .ant-steps-item-title {
      font-size: 14px;
    }
    .ant-steps-item-description {
      font-size: 12px;
    }
  }
`;

const StyledCard = styled(Card)<{ $isCompleted: boolean }>`
  background: ${(props) => (props.$isCompleted ? '#52c41a' : '#fce248')};
  border: 1px solid ${(props) => (props.$isCompleted ? '#52c41a' : '#ffaa00')};
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  @media (max-width: 768px) {
    padding: 12px;
  }
  .ant-card-head {
    background: ${(props) => (props.$isCompleted ? '#52c41a' : '#fce248')};
    border-bottom: none;
  }
  .ant-card-head-title {
    color: #0427f4;
    font-weight: 600;
  }
`;

const StatusText = styled(Text)`
  color: #0427f4;
  font-weight: bold;
  margin-left: 10px;
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;
  .ant-breadcrumb a {
    color: #0427f4;
    transition: color 0.3s ease;
  }
  .ant-breadcrumb a:hover {
    color: #40c4ff;
  }
  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const InitiProcess: React.FC = () => {
  const userStore = useUserStore();
  const navigate = useNavigate();

  const verificationStatus = userStore.verified
    ? 'Completed'
    : userStore.submittedForVerification
    ? 'Pending'
    : 'Not Started';

  const evaluationStatus = userStore.verified ? 'Pending' : 'Not Started'; // Placeholder for future evaluation status logic

  // Calculate progress: 0 (Not Started), 1 (Verification Completed), etc.
  const currentStep = userStore.verified ? 1 : 0; // Only Verification has a completion condition for now

  const steps = [
    {
      title: 'Verification',
      description: `Status: ${verificationStatus}`,
      status: verificationStatus === 'Completed' ? 'finish' : verificationStatus === 'Pending' ? 'process' : 'wait',
      isCompleted: verificationStatus === 'Completed',
      onClick: () => {
        console.log('InitiProcess: Navigating to /verification');
        navigate('/verification');
      },
    },
    {
      title: 'Evaluation',
      description: `Status: ${evaluationStatus}`,
      status: currentStep >= 1 ? 'process' : 'wait',
      isCompleted: false, // Placeholder: Update when evaluation status logic is added
      onClick: () => {
        if (userStore.verified || userStore.submittedForVerification) {
          console.log('InitiProcess: Navigating to /home/evaluationS');
          navigate('/home/evaluationS');
        } else {
          console.log('InitiProcess: Evaluation navigation blocked - user not verified or pending');
        }
      },
    },
    {
      title: 'Schedule',
      description: 'Status: Pending',
      status: 'wait',
      isCompleted: false, // Placeholder
      onClick: () => {}, // Not clickable for now
    },
    {
      title: 'Intro and Initi',
      description: 'Status: Pending',
      status: 'wait',
      isCompleted: false, // Placeholder
      onClick: () => {}, // Not clickable for now
    },
  ];

  const breadcrumbItems = [
    {
      title: (
        <Link to="/">
          <HomeOutlined /> Home
        </Link>
      ),
    },
    {
      title: <Link to="/home/ols">Main Gate</Link>,
    },
    {
      title: 'Initi',
    },
    {
      title: <Link to="/verification">Verification</Link>,
    },
    (userStore.verified || userStore.submittedForVerification) && userStore.userRole?.toLowerCase() === UserRole.OLSS_COL.toLowerCase() && {
      title: <Link to="/home/evaluationS">Evaluation</Link>,
    },
  ].filter(Boolean);

  console.log(
    'InitiProcess rendering - verificationStatus:', verificationStatus,
    'evaluationStatus:', evaluationStatus,
    'currentStep:', currentStep,
    'breadcrumbItems:', breadcrumbItems,
    'userRole:', userStore.userRole
  );

  return (
    <InitiProcessContainer className="initi-process">
      <Helmet>
        <title>OLS Initi Process - One Light System</title>
        <meta
          name="description"
          content="Understand the OLS tuition process for Sun Light Meditation education through the initiation steps."
        />
        <meta
          name="keywords"
          content="OLS Initi Process, Sun Light Meditation, OLS Verification, OLS Tuition"
        />
        <link rel="canonical" href="https://www.olsme.com/initi" />
      </Helmet>
      <BreadcrumbWrapper>
        <Breadcrumb items={breadcrumbItems} />
      </BreadcrumbWrapper>
      <CenteredContent>
        <CenteredTitle level={2}>OLS Initi Process</CenteredTitle>
        <Text style={{ color: '#0427f4', fontSize: '16px', display: 'block', marginBottom: '20px' }}>
          Welcome to the OLS tuition process! Follow the steps below to become a fully initiated student or olsme subscription user.
        </Text>
        <StepsWrapper>
          <Steps current={currentStep} responsive>
            {steps.map((step, index) => (
              <Step
                key={index}
                title={step.title}
                description={step.description}
                status={step.status}
                icon={step.isCompleted ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
              />
            ))}
          </Steps>
        </StepsWrapper>
        <Space direction="vertical" style={{ width: '100%' }}>
          {steps.map((step, index) => (
            <StyledCard
              key={index}
              title={step.title}
              $isCompleted={step.isCompleted}
              onClick={step.onClick}
              aria-label={`Go to ${step.title.toLowerCase()} page`}
              hoverable={!!step.onClick}
            >
              <Text style={{ color: '#0427f4', fontSize: '14px' }}>
                {step.description}
              </Text>
            </StyledCard>
          ))}
        </Space>
      </CenteredContent>
    </InitiProcessContainer>
  );
};

export default observer(InitiProcess);